/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState } from "react";
import Pagination from "../../../../widgets/pagination";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import FilterBar from "../media_powder_inventory/partials/filterBar"; 
import MediaPreparationTable from "../../../common/commonComponents/mediaPreparationTable";
import StatusCard from "../../../common/commonComponents/statusCard";
import { useTranslation } from 'react-i18next';
import { MediaSolutionService } from "../../../../service/MediaSolutionServices";
import { useEffect } from "react";
import { getColumns } from "../solution_reagent_inventory/partials/column";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import LogMediaPowderModal from "../media_powder_inventory/partials/logMediaModal";
import prepareSolution from "./views/prepareSolution";
import SolutionLotDetails from "./views/solutionLotDetails";
import CustomToast from "../../../../widgets/toaster";
import { useNavigate } from "react-router-dom";
import { userService } from "../../../../service/userService";


const SolutionInventory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { t } = useTranslation();  
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [lotFilter, setLotFilter] = useState('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [batchCodes, setBatchCodes] = useState<string[]>([]);
  const [showLogModal, setShowLogModal] = useState(false);

  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [mediaTypes, setMediaTypes] = useState<any[]>([]);
  const [view, setView] = useState<"list" | "lot">("list");
  const [selectedLot, setSelectedLot] = useState<any>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [permission, setPermission] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState("id");
  const [order, setOrder] = useState("desc");
  const handleSort = (key: string, value: boolean) => {
    setSort(key);
    setOrder(value ? "asc" : "desc");
    setCurrentPage(1);
  };


  const getPermissionByRole = async (role: number) => {
    try {
      const response = await userService.getPermissionByRole(role);

      if (response.status && response.statusCode === 200) {
        setPermission(response.data.permissions);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const storedData = localStorage.getItem("userDetails");

    if (storedData) {
      const storedObject = JSON.parse(storedData);
      getPermissionByRole(storedObject[0].role_master_id);
    }
  }, []);

  const [stats, setStats] = useState({
    approved: 0,
    expired: 0,
    rejected: 0,
  });

  const mapStatusToApi = (status: string) => {
    if (status === "Expired") return "expired";
    return status;
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);

    if (size) {
      setPageSize(size);
    }
  };

  const getMediaSolutionStats = async () => {
    try {
        const response = await MediaSolutionService.getMediaSolutionStats();

        if (response?.status && response.statusCode === 200) {
        const data = response.data;
        setStats({ approved: data.available_for_use, expired: data.expired, rejected: data.blocked });
        }
    } catch (error) {
        console.error("Stats API error:", error);
    }
  };


  // get solution batch list
  const getMediaSolutionList = async () => {
    setLoading(true);
    try {
        const response = await MediaSolutionService.getMediaSolutionList(
        currentPage,
        pageSize,
        sort,
        order,
        searchTerm,
        statusFilter ? mapStatusToApi(statusFilter) : "",
        lotFilter,
        mediaTypeFilter,
        fromDate,
        toDate
        );

        if (response?.status && response.statusCode === 200) {
        const mappedData = response.data.map((item: any) => ({
            id: item.id,
            batchCode: item.batch_name,
            name: item.solution_name,
            mf: item.supplier_name,
            storage: item.storage_temperature,
            stock: item.available_stock,
            expiry: item.expiry_date,
            qaStatus: item.qa_release_status,
            stockstatus: item.stock_status,
        }));

        setInventoryItems(mappedData);
        setTotalPages(response.totalPages);
        } else {
        setInventoryItems([]);
        }
    } catch (error) {
        console.error("List API error:", error);
    } finally {
      setLoading(false);
    }
    };


    // batch code list for filter dropdown 
  const getBatchCodes = async () => {
    try {
      const response = await MediaSolutionService.getBatchCodes();

      if (response.status && response.statusCode === 200) {
          const codes = response.data.map((item: any) => item.batch_name);
          setBatchCodes(codes);
        }
      } catch (error) {
        console.error("Batch code API error:", error);
    }
  };

  // media solution types for filter dropdown
  const getMediaSolutionTypes = async () => {
    try {
      const response = await MediaSolutionService.getMediaSolutionTypeList();

      if (response?.status && response.statusCode === 200) {
        const formatted = response.data.map((item: any) => ({
          id: item.id,
          name: item.name, 
        }));

        setMediaTypes(formatted);
      } else {
        setMediaTypes([]);
      }
    } catch (error) {
      console.error("Solution type API error:", error);
    }
  };
  
  useEffect(() => {
    getMediaSolutionStats();
  }, []);

  useEffect(() => {
    getMediaSolutionList();
  }, [currentPage, pageSize,sort, order, searchTerm, statusFilter, lotFilter, mediaTypeFilter, fromDate, toDate]); 

  useEffect(() => {
      getBatchCodes();
  }, []);


  useEffect(() => {
    getMediaSolutionTypes();
  }, []);


  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setLotFilter('');
    setMediaTypeFilter('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const showToastMessage = (message: string, type: string) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };


  const handleItemsPerPageChange = (items: number) => {
      setPageSize(items);
      setCurrentPage(1);
    };

    if (view === "lot" && selectedLot) {
    return (
      <SolutionLotDetails
        lotId={selectedLot.id}
        stockStatus={selectedLot.stockstatus}
        batchName={selectedLot.batchCode}
        mediaName={selectedLot.name}
        onBack={() => {
          setView("list");
          getMediaSolutionList();
          getMediaSolutionStats();
          getBatchCodes();
        }}
      />
    );
  }

  return (
    <div className="media-prep-scope">
      <MediaPrepHeaderSection />
      <MediaPrepPageHeader pageHeader={t('solutionInventory.title')} pageDescription={t('pageDescription.solutionReagentInventory.description')} />
      <div className="media-prep-scope px-8 pb-13 pt-3 max-w-full animate-in fade-in duration-500 min-h-full">
      <div className="flex items-start justify-between mb-2">
        <div>
        <div className="flex items-center gap-3 mb-1">

            <div className="space-y-1">
            {/* <h2 className='headText1 m-0 d-flex align-items-center' style={{ fontSize: '18px', height: '100%' }}>
                {t('solutionInventory.title')}
            </h2>
            <p className="text-slate-400 text-[12.5px] font-medium">
                {t('solutionInventory.description')}
            </p> */}
            </div>
        </div>
        </div>
        <div className="flex items-center gap-4 mt-1 shrink-0">
          {permission?.includes("reconcile_stock_solution") && (
          <button 
            onClick={() => navigate("/reconcileSolution")}
            className="bg-white text-slate-700 border border-slate-200 flex items-center gap-2 px-3 py-1 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm focus:outline-none focus:ring-0 active:outline-none"
          >
            <span className="material-symbols-outlined text-xl">{t('solutionInventory.icons.rebase_edit')}</span> {t('solutionInventory.actions.reconcileStock')}
          </button>
          )}
          {permission?.includes("add_media_solution_inventory") && (
            <button 
              onClick={() => setShowLogModal(true)}
              className="bg-[#FF7043] text-white flex items-center gap-1 px-2 py-2 rounded-lg shadow-[0_8px_20px_rgba(255,112,67,0.2)] font-semibold text-xs uppercase tracking-wide hover:brightness-110 active:scale-95 transition-all border-none"
            >
              <span className="material-symbols-outlined">{t('solutionInventory.icons.add_box')}</span> {t('solutionInventory.actions.logShipment')}
            </button>
          )}
        </div>
      </div>

        <div className="grid grid-cols-3 gap-6 mb-3.5">
          {[
            { label: t('solutionInventory.stats.available'), val: stats.approved, color: "#5a9b85", icon: "check_circle", trend: t('solutionInventory.stats.qcApproved'), status: t('solutionInventory.stats.Released') },
            { label: t('solutionInventory.stats.expired'), val: stats.expired, color: "#c76099", icon: "hourglass_empty", trend: t('solutionInventory.stats.expired'), status: t('solutionInventory.stats.expired') },
            { label: t('solutionInventory.stats.rejected'), val: stats.rejected, color: "#b18181", icon: "cancel", trend: t('solutionInventory.stats.rejected'), status: t('solutionInventory.stats.Rejected') },
          ].map((box, i) => (
            <StatusCard
              key={i}
              label={box.label}
              value={box.val}
              color={box.color}
              icon={box.icon}
              trend={box.trend}
              status={box.status}
              selectedStatus={statusFilter}
              onClick={(status) =>
                setStatusFilter(statusFilter === status ? "" : status)
              }
            />
          ))}
        </div>

        {/* FILTER */}
        <FilterBar
          batchCodes={batchCodes}
          statuses={["Released", "Rejected", "Expired"]}
          mediaTypes={mediaTypes}
          selectedBatchCode={lotFilter}
          selectedStatus={statusFilter}
          selectedMediaType={mediaTypeFilter}
          fromDate={fromDate}
          toDate={toDate}
          searchTerm={searchInput}

          onBatchCodeChange={(val) => {
            setLotFilter(val);
            setCurrentPage(1);
          }}

          onStatusChange={(val) => {
            setStatusFilter(val);
            setCurrentPage(1);
          }}

          onMediaTypeChange={(val) => {
            setMediaTypeFilter(val);
            setCurrentPage(1);
          }}

          onFromDateChange={(val) => {
            setFromDate(val);
            setCurrentPage(1);
          }}

          onToDateChange={(val) => {
            setToDate(val);
            setCurrentPage(1);
          }}

          onSearchChange={(val) => {
            setSearchInput(val);

            if (!val) {
              setSearchTerm('');
              setCurrentPage(1);
            }
          }}

          onSearchClick={() => {
            setSearchTerm(searchInput);
            setCurrentPage(1);
          }}

          onClearFilters={handleClearFilters}
        />

        {/* TABLE */}
        <MediaPreparationTable
          columns={getColumns(t)}
          data={inventoryItems}
          loading={loading}
          currentPage={currentPage}
          pageSize={pageSize}
          onSortClick={handleSort}
          renderAction={(row) => (
            <button
              onClick={() => {
                setSelectedLot(row);
                setView("lot");
              }}
              className="flex items-center justify-center gap-1 w-[90px] h-[26px] bg-[rgb(54,64,93)] text-white rounded-lg font-black text-[8px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none outline-none focus:outline-none"
            >
              <span className="material-symbols-outlined text-base">
                {t('mediaPowder.icons.visibility')}
              </span>
              {t('mediaPowder.actions.view')}
            </button>
          )}
        />

        <CustomToast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={2500}
          message={toastMessage}
          toastType={toastType}
        />

        <LogMediaPowderModal
          show={showLogModal}
          onClose={() => setShowLogModal(false)}
          onSuccess={() => {
            setShowLogModal(false);
            getMediaSolutionList();
            getMediaSolutionStats();
            getBatchCodes();
          }}
          title={t('solutionInventory.form.title')}
          FormComponent={prepareSolution}
          formProps={{ showToastMessage }}
        />
        <Pagination
          page={currentPage}
          totalPages={totalPages || 1}
          handlePageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
export default SolutionInventory;